import React, { useCallback, useEffect, useRef, useState } from 'react';

const GSI_SRC = 'https://accounts.google.com/gsi/client';

// Unset means the deployment has no Google Workspace wired up: the button renders
// nothing at all rather than a control that can only fail. Exported so Login.js can
// also drop the "or" divider instead of leaving one stranded above the form.
export const isGoogleSignInEnabled = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Google's button is drawn by their script into a DOM node we hand over, and their
// script must be loaded exactly once per page. A module-level promise gives every
// mount the same load, so remounting (React 18 StrictMode double-invokes effects in
// development, and /login remounts on every failed attempt) does not append a second
// <script> tag.
let gsiPromise;

const loadGsi = () => {
  if (gsiPromise) return gsiPromise;
  gsiPromise = new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = GSI_SRC;
    el.async = true;
    el.defer = true;
    el.onload = resolve;
    el.onerror = () => {
      // Drop the cached rejection so a later mount can retry - the usual cause is a
      // transient network failure, not a permanent one.
      gsiPromise = undefined;
      reject(new Error('Failed to load Google Identity Services'));
    };
    document.head.appendChild(el);
  });
  return gsiPromise;
};

// Google's button only accepts a width in px, between 200 and 400, and does not reflow
// on its own. Measuring the container keeps it flush with the full-width submit button
// below it instead of sitting narrower than everything else in the card.
const buttonWidth = (node) => Math.max(200, Math.min(400, Math.round(node.offsetWidth) || 400));

/**
 * The "Sign in with Google" button. Hands the parent the ID token Google returns and
 * takes no view on what it means - verifying it and deciding whether the address maps
 * to an HRMS account is the server's job (POST /api/auth/google).
 */
const GoogleSignInButton = ({ onCredential }) => {
  const containerRef = useRef(null);
  const [unavailable, setUnavailable] = useState(false);

  // Held in a ref so a parent re-render (which produces a fresh onCredential closure)
  // does not re-run the effect and tear Google's button down mid-click.
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;

  const render = useCallback(() => {
    const node = containerRef.current;
    if (!node || !window.google?.accounts?.id) return;
    window.google.accounts.id.renderButton(node, {
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'center',
      width: buttonWidth(node),
    });
  }, []);

  useEffect(() => {
    if (!isGoogleSignInEnabled) return undefined;

    let cancelled = false;
    let observer;

    loadGsi()
      .then(() => {
        if (cancelled || !containerRef.current || !window.google?.accounts?.id) return;

        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: (response) => onCredentialRef.current?.(response.credential),
          // One Tap and auto-select are deliberately off. Signing someone in the
          // instant they land on /login - including right after they logged out -
          // is surprising, and this app has exactly one signed-in identity at a time.
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        render();

        // Redraw on width changes (phone rotation, window resize) since Google's
        // button is a fixed-px iframe that would otherwise stay at its first size.
        if (typeof ResizeObserver !== 'undefined') {
          let lastWidth = buttonWidth(containerRef.current);
          observer = new ResizeObserver(() => {
            if (!containerRef.current) return;
            const width = buttonWidth(containerRef.current);
            if (width === lastWidth) return;
            lastWidth = width;
            render();
          });
          observer.observe(containerRef.current);
        }
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [render]);

  if (!isGoogleSignInEnabled) return null;

  return (
    <div data-testid="login-google">
      {/* min-height matches Google's large button so the card does not jump as the
          script loads. */}
      <div ref={containerRef} className="flex justify-center min-h-[44px]" />
      {unavailable && (
        <p className="mt-2 text-center text-sm text-gray-500">
          Google sign-in is unavailable right now. Please use your username and password.
        </p>
      )}
    </div>
  );
};

export default GoogleSignInButton;
